import React, { useState, useEffect, useRef } from 'react';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, query, where } from 'firebase/firestore';
import { useLocation, useNavigate } from 'react-router-dom';
import { generateMonthlyPDF } from '../services/pdfGenerator';
import {
  FaCalendarPlus,
  FaTrash,
  FaWhatsapp,
  FaCheck,
  FaExternalLinkAlt,
  FaHistory,
  FaEdit,
  FaSearch,
  FaTimes,
  FaFilter,
  FaExclamationTriangle,
  FaMagic,
  FaCalendarTimes,
  FaFilePdf,
  FaCheckCircle,
  FaExclamationCircle,
  FaUserClock,
  FaYoutube,
  FaGuitar,
  FaCloud,
  FaHeadphones
} from 'react-icons/fa';


// Ordem de prioridade para exibição da equipe
const ROLE_ORDER = [
  'Ministro de Música',
  'Violão',
  'Teclado',
  'Baixo',
  'Bateria',
  'Guitarra',
  'Sax',
  'Vocal',
];

function sortTeam(team) {
  if (!team) return [];
  return [...team].sort((a, b) => {
    const indexA = ROLE_ORDER.indexOf(a.role);
    const indexB = ROLE_ORDER.indexOf(b.role);
    const orderA = indexA === -1 ? ROLE_ORDER.length - 1.5 : indexA;
    const orderB = indexB === -1 ? ROLE_ORDER.length - 1.5 : indexB;
    return orderA - orderB;
  });
}

export default function Schedules() {
  const { userDetails } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // === ESTADOS DE DADOS ===
  const [schedules, setSchedules] = useState([]);
  const [musicians, setMusicians] = useState([]);
  const [songs, setSongs] = useState([]);
  const [cults, setCults] = useState([]);
  const [loading, setLoading] = useState(true);

  // === ESTADOS DE UI ===
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [hasHandledDashboardRequest, setHasHandledDashboardRequest] = useState(false);
  const formTopRef = useRef(null);

  // === ESTADOS DE FILTRO ===
  const todayDate = new Date();
  const [filterYear, setFilterYear] = useState(String(todayDate.getFullYear()));
  const [filterMonth, setFilterMonth] = useState(String(todayDate.getMonth() + 1).padStart(2, '0'));

  // === ESTADOS DO FORMULÁRIO ===
  const [title, setTitle] = useState('');
  const [cultSelectValue, setCultSelectValue] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');

  const [selectedTeam, setSelectedTeam] = useState([]);
  const [selectedSongs, setSelectedSongs] = useState([]);
  const [songSearchTerm, setSongSearchTerm] = useState('');

  // === ESTADOS DE BLOQUEIO ===
  const [unavailableMusicians, setUnavailableMusicians] = useState([]);
  const [busyMusicians, setBusyMusicians] = useState([]);

  const schedulesRef = collection(db, "schedules");
  const musiciansRef = collection(db, "musicians");
  const songsRef = collection(db, "songs");
  const unavailRef = collection(db, "unavailability");
  const cultsRef = collection(db, "service_templates");

  // === LISTA DE FUNÇÕES QUE PERMITEM MÚLTIPLOS ===
  // CORREÇÃO: Removido 'Ministro de Música' daqui. Agora ele é único por escala.
  const MULTI_PLAYER_ROLES = ['Vocal', 'Backing Vocal', 'Coral'];

  const showFeedback = (msg, type = 'success') => {
    setFeedback({ msg, type });
    setTimeout(() => setFeedback(null), 4000);
  };

  // === CARREGAR DADOS ===
  const loadData = async () => {
    setLoading(true);
    try {
      const schedData = await getDocs(schedulesRef);
      const schedList = schedData.docs.map(doc => ({ ...doc.data(), id: doc.id }));
      setSchedules(schedList);

      const musData = await getDocs(musiciansRef);
      const mList = musData.docs.map(doc => ({ ...doc.data(), id: doc.id }));
      mList.sort((a, b) => a.name.localeCompare(b.name));
      setMusicians(mList);

      const songData = await getDocs(songsRef);
      const sList = songData.docs.map(doc => ({ ...doc.data(), id: doc.id }));
      sList.sort((a, b) => a.title.localeCompare(b.title));
      setSongs(sList);

      const cultData = await getDocs(cultsRef);
      const cList = cultData.docs.map(doc => ({ ...doc.data(), id: doc.id }));
      cList.sort((a, b) => a.name.localeCompare(b.name));
      setCults(cList);

    } catch (error) {
      console.error("Erro ao carregar:", error);
      showFeedback("Erro de conexão.", "error");
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCultSelection = (e) => {
    const selectedValue = e.target.value;
    setCultSelectValue(selectedValue);
    if (selectedValue === 'custom') {
      setTitle(''); setTime('');
    } else {
      setTitle(selectedValue);
      const foundCult = cults.find(c => c.name === selectedValue);
      if (foundCult) setTime(foundCult.time);
    }
  };

  // === CHECK 1: INDISPONIBILIDADE ===
  useEffect(() => {
    const checkAvailability = async () => {
      if (!date) { setUnavailableMusicians([]); return; }
      try {
        const q = query(unavailRef, where("date", "==", date));
        const snap = await getDocs(q);
        // AGORA SALVA O OBJETO INTEIRO (ID E PERÍODO TBM) PARA AVALAÇÃO DE DOMINGO
        const records = snap.docs.map(d => ({
          id: d.data().musicianId,
          servicePeriod: d.data().servicePeriod || 'Ambos'
        }));
        setUnavailableMusicians(records);
      } catch (e) { console.error(e); }
    };
    checkAvailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  // === CHECK 2: CHOQUE DE HORÁRIO ===
  useEffect(() => {
    const checkBusyStatus = async () => {
      if (!date || !time) { setBusyMusicians([]); return; }
      try {
        const q = query(schedulesRef, where("date", "==", date), where("time", "==", time));
        const snap = await getDocs(q);
        let busyIds = [];
        snap.forEach(docSnapshot => {
          if (editId && docSnapshot.id === editId) return;
          const schedData = docSnapshot.data();
          if (schedData.team) {
            const idsInThisSchedule = schedData.team.map(m => m.id);
            busyIds = [...busyIds, ...idsInThisSchedule];
          }
        });
        setBusyMusicians(busyIds);
      } catch (e) { console.error("Erro busy:", e); }
    };
    const timer = setTimeout(() => { checkBusyStatus(); }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, time, editId]); // Removido schedulesRef da dependência para evitar loop, mas mantido funcional

  // Auto-Open Edit
  useEffect(() => {
    if (!loading && location.state?.editScheduleId && schedules.length > 0 && !hasHandledDashboardRequest) {
      const targetSchedule = schedules.find(s => s.id === location.state.editScheduleId);
      if (targetSchedule) {
        handleEdit(targetSchedule);
        setHasHandledDashboardRequest(true);
        window.history.replaceState({}, document.title);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, location.state, schedules, hasHandledDashboardRequest]);

  // Helpers
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    const dateObj = new Date(year, month - 1, day);
    return dateObj.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' });
  };

  const isPastDate = (dateString) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const [year, month, day] = dateString.split('-');
    const eventDate = new Date(year, month - 1, day);
    return eventDate < today;
  };

  const canEditSchedule = (schedule) => {
    if (!userDetails) return false;
    if (userDetails.isAdmin) return true;
    if (schedule.team && userDetails.musicianId) {
      const myEntry = schedule.team.find(m => m.id === userDetails.musicianId);
      if (myEntry && myEntry.role === "Ministro de Música") return true;
    }
    return false;
  };

  const getMemberStyle = (member) => {
    if (!userDetails) return "";
    const isMe = member.id === userDetails.musicianId;
    const isMinister = member.role === "Ministro de Música";
    if (isMe) return "bg-brand-blue text-white border-brand-blue shadow-sm";
    if (isMinister) return "bg-orange-100 text-orange-900 border-orange-200";
    return "bg-brand-gold/10 text-yellow-800 border-brand-gold/20";
  };

  // Filtros
  const selectedYearMonth = `${filterYear}-${filterMonth}`;
  const filteredSchedules = schedules.filter(s => s.date && s.date.startsWith(selectedYearMonth));
  const futureSchedules = filteredSchedules.filter(s => !isPastDate(s.date));
  const pastSchedules = filteredSchedules.filter(s => isPastDate(s.date));

  const sortSchedulesByDateAndTime = (a, b) => {
    if (a.date !== b.date) {
      return new Date(a.date) - new Date(b.date);
    }
    const timeA = a.time || '00:00';
    const timeB = b.time || '00:00';
    return timeA.localeCompare(timeB);
  };

  futureSchedules.sort(sortSchedulesByDateAndTime);
  pastSchedules.sort(sortSchedulesByDateAndTime);
  const displaySchedules = [...futureSchedules, ...pastSchedules];

  const handleDownloadPDF = async () => {
    if (displaySchedules.length === 0) {
      showFeedback("Sem dados para PDF.", "error");
      return;
    }
    const monthLabel = months.find(m => m.val === filterMonth)?.label || filterMonth;

    showFeedback("Gerando PDF...", "success");
    const resultMessage = await generateMonthlyPDF(displaySchedules, `${monthLabel}/${filterYear}`);
    if (resultMessage) showFeedback(resultMessage);
  };

  // === ADICIONAR MÚSICO ===
  const addMusicianToTeam = (musician) => {
    if (selectedTeam.some(m => m.id === musician.id)) return;

    if (busyMusicians.includes(musician.id)) {
      alert(`BLOQUEADO: ${musician.name} já está escalado neste horário.`);
      return;
    }

    const unavailRecord = unavailableMusicians.find(u => u.id === musician.id);
    if (unavailRecord) {
      let isActuallyUnavailable = true;

      // Se for domingo, checa os turnos
      if (date && time) {
        const [y, mo, d] = date.split('-');
        const dateObj = new Date(y, mo - 1, d);

        if (dateObj.getDay() === 0) {
          const isMorningService = time < "14:00";
          const isEveningService = time >= "14:00";

          if (unavailRecord.servicePeriod === 'Manhã' && isEveningService) isActuallyUnavailable = false;
          if (unavailRecord.servicePeriod === 'Noite' && isMorningService) isActuallyUnavailable = false;
        }
      }

      if (isActuallyUnavailable) {
        if (!window.confirm(`⚠️ ALERTA: ${musician.name} marcou indisponibilidade neste horário! Deseja escalar?`)) return;
      }
    }

    let defaultRole = "Músico";
    if (musician.primaryRole) defaultRole = musician.primaryRole;
    else if (Array.isArray(musician.roles) && musician.roles.length > 0) defaultRole = musician.roles[0];
    else if (musician.role) defaultRole = musician.role;

    // Validação de Instrumento Único (INCLUI MINISTRO AGORA)
    if (!MULTI_PLAYER_ROLES.includes(defaultRole)) {
      const alreadyHasRole = selectedTeam.some(m => m.role === defaultRole);
      if (alreadyHasRole) {
        alert(`BLOQUEADO: Já existe um(a) ${defaultRole} nesta escala.`);
        return;
      }
    }

    setSelectedTeam([...selectedTeam, { id: musician.id, name: musician.name, role: defaultRole, availableRoles: musician.roles || [musician.role] }]);
  };

  const removeMusicianFromTeam = (id) => { setSelectedTeam(selectedTeam.filter(m => m.id !== id)); };

  const changeMusicianRole = (id, newRole) => {
    // Validação ao trocar no dropdown (INCLUI MINISTRO AGORA)
    if (!MULTI_PLAYER_ROLES.includes(newRole)) {
      const alreadyHasRole = selectedTeam.some(m => m.role === newRole && m.id !== id);
      if (alreadyHasRole) {
        alert(`BLOQUEADO: Já existe um(a) ${newRole} nesta escala.`);
        return;
      }
    }
    setSelectedTeam(selectedTeam.map(m => m.id === id ? { ...m, role: newRole } : m));
  };

  const toggleSong = (song) => {
    if (selectedSongs.some(s => s.id === song.id)) {
      setSelectedSongs(selectedSongs.filter(s => s.id !== song.id));
    } else {
      // Quando adiciona, guarda o tom original como tom padrão desta escala
      setSelectedSongs([...selectedSongs, { ...song, customKey: song.key }]);
    }
  };

  const updateSongKey = (id, newKey) => {
    setSelectedSongs(selectedSongs.map(s => s.id === id ? { ...s, customKey: newKey } : s));
  };

  const filteredSongsList = songSearchTerm.length >= 3
    ? songs.filter(s => s.title.toLowerCase().includes(songSearchTerm.toLowerCase()) || (s.artist && s.artist.toLowerCase().includes(songSearchTerm.toLowerCase())))
    : [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title || !date) return;
    try {
      const hasMinister = selectedTeam.some(m => m.role === 'Ministro de Música');
      const isMissingSomething = !hasMinister || selectedTeam.length === 0 || selectedSongs.length === 0;

      const scheduleData = {
        title, date, time,
        team: selectedTeam,
        repertoire: selectedSongs,
        updatedAt: new Date(),
        hasGaps: isMissingSomething
      };

      if (editId) {
        await updateDoc(doc(db, "schedules", editId), scheduleData);
        showFeedback("Escala atualizada!");
      } else {
        await addDoc(schedulesRef, { ...scheduleData, createdAt: new Date() });
        showFeedback("Nova escala criada!");
      }
      handleCancel(); loadData();
    } catch (error) { console.error(error); showFeedback("Erro ao salvar.", "error"); }
  };

  const handleEdit = (sched) => {
    setTitle(sched.title); setDate(sched.date); setTime(sched.time);

    const isStandard = cults.some(c => c.name === sched.title);
    setCultSelectValue(isStandard ? sched.title : 'custom');

    if (sched.team) {
      const hydratedTeam = sched.team.map(member => {
        const originalMusician = musicians.find(m => m.id === member.id);
        return { ...member, availableRoles: originalMusician ? (originalMusician.roles || [originalMusician.role]) : [] };
      });
      setSelectedTeam(hydratedTeam);
    } else { setSelectedTeam([]); }

    // Assegurar compatibilidade com escalas antigas que não tinham customKey/artist
    const hydratedSongs = (sched.repertoire || []).map(s => {
      const liveSong = songs.find(song => song.id === s.id) || {};
      return {
        ...s,
        customKey: s.customKey || s.key,
        artist: liveSong.artist || s.artist || ''
      };
    });
    setSelectedSongs(hydratedSongs);

    setEditId(sched.id); setIsFormOpen(true);
    setTimeout(() => { if (formTopRef.current) formTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 100);
  };

  const handleCancel = () => {
    setTitle(''); setDate(''); setTime(''); setSelectedTeam([]); setSelectedSongs([]); setSongSearchTerm(''); setCultSelectValue('');
    setEditId(null); setIsFormOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (window.confirm("Apagar esta escala?")) { await deleteDoc(doc(db, "schedules", id)); showFeedback("Excluído."); loadData(); }
  };

  const generateWhatsappLink = (schedule) => {
    let text = `*ESCALA - ${schedule.title}*\n`;
    text += `📅 Data: ${formatDate(schedule.date)} às ${schedule.time}\n\n`;
    text += `*🎵 Repertório:*\n`;
    schedule.repertoire.forEach(song => {
      const keyToUse = song.customKey || song.key || '?';
      text += `• ${song.title}${song.artist ? ` - ${song.artist}` : ''} (${keyToUse})\n`;
      if (song.link) text += `  Link: ${song.link}\n`;
    });
    text += `\n*🎸 Equipe:*\n`;
    sortTeam(schedule.team).forEach(member => { text += `• ${member.name} (${member.role})\n`; });
    return `https://wa.me/?text=${encodeURIComponent(text)}`;
  };

  const months = [{ val: '01', label: 'Janeiro' }, { val: '02', label: 'Fevereiro' }, { val: '03', label: 'Março' }, { val: '04', label: 'Abril' }, { val: '05', label: 'Maio' }, { val: '06', label: 'Junho' }, { val: '07', label: 'Julho' }, { val: '08', label: 'Agosto' }, { val: '09', label: 'Setembro' }, { val: '10', label: 'Outubro' }, { val: '11', label: 'Novembro' }, { val: '12', label: 'Dezembro' }];
  const years = [todayDate.getFullYear() - 1, todayDate.getFullYear(), todayDate.getFullYear() + 1];

  const isReadOnly = !userDetails?.isAdmin;

  return (
    <div>
      {feedback && (<div className={`fixed top-4 left-1/2 transform -translate-x-1/2 px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2 animate-fade-in-down ${feedback.type === 'error' ? 'bg-red-500 text-white' : 'bg-green-500 text-white'}`}>{feedback.type === 'error' ? <FaExclamationCircle /> : <FaCheckCircle />}<span className="font-bold">{feedback.msg}</span></div>)}

      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
        <div><h1 className="text-2xl font-bold text-gray-800">Agenda & Escalas</h1></div>
        <div className="flex flex-col items-end gap-2 w-full md:w-auto">
          <div className="flex items-center gap-2 w-full md:w-auto justify-end">
            <div className="flex items-center bg-white border border-gray-300 rounded-lg px-3 py-2 shadow-sm focus-within:border-brand-blue overflow-hidden">
              <div className="pl-2 text-gray-500"><FaFilter /></div>
              <select value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)} className="py-2 px-2 bg-transparent outline-none text-gray-700 text-sm cursor-pointer">{months.map(m => <option key={m.val} value={m.val}>{m.label}</option>)}</select>
              <div className="text-gray-300">|</div>
              <select value={filterYear} onChange={(e) => setFilterYear(e.target.value)} className="py-2 px-2 bg-transparent outline-none text-gray-700 text-sm cursor-pointer">{years.map(y => <option key={y} value={y}>{y}</option>)}</select>
            </div>
            {userDetails?.isAdmin && (<button onClick={() => { if (isFormOpen) handleCancel(); else setIsFormOpen(true); }} className="bg-brand-blue text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-800 transition shadow whitespace-nowrap"><FaCalendarPlus /><span className="hidden sm:inline">{isFormOpen ? 'Cancelar' : 'Nova'}</span></button>)}
          </div>
          <div className="flex gap-2 flex-wrap justify-end">
            <button onClick={() => navigate('/indisponibilidade')} className="text-xs text-red-500 hover:text-red-700 font-bold flex items-center gap-1 bg-red-50 px-3 py-1 rounded-full border border-red-100 hover:border-red-200 transition"><FaCalendarTimes /> Indisponibilidades</button>
            <button onClick={handleDownloadPDF} className="text-xs text-red-600 hover:text-red-800 font-bold flex items-center gap-1 bg-red-50 px-3 py-1 rounded-full border border-red-100 hover:border-red-200 transition"><FaFilePdf /> Baixar PDF</button>
            {userDetails?.isAdmin && (<button onClick={() => navigate('/gerador')} className="text-xs text-brand-blue hover:text-blue-800 font-bold flex items-center gap-1 bg-blue-50 px-3 py-1 rounded-full border border-blue-100 hover:border-blue-200 transition"><FaMagic className="text-brand-gold" /> Abrir geração de escalas automáticas</button>)}
          </div>
        </div>
      </div>

      <div ref={formTopRef}></div>

      {isFormOpen && (
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg mb-8 border-t-4 border-brand-blue animate-fade-in overflow-hidden">
          <h3 className="text-xl font-bold mb-6 text-gray-800">{editId ? 'Editar Escala' : 'Montar Nova Escala'}</h3>
          {isReadOnly && (<div className="mb-4 bg-yellow-50 border-l-4 border-yellow-400 p-4"><p className="text-sm text-yellow-700"><strong>Modo Ministro de Música:</strong> Você só pode editar o repertório.</p></div>)}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-bold text-gray-600 mb-1">Culto/Evento</label>
                <div className="relative">
                  <select value={cultSelectValue} onChange={handleCultSelection} disabled={isReadOnly} className={`w-full border p-2 rounded appearance-none bg-white ${isReadOnly ? 'bg-gray-100 text-gray-500' : ''}`} required>
                    <option value="">Selecione...</option>
                    {cults.map(c => (<option key={c.id} value={c.name}>{c.name}</option>))}
                    <option value="custom" className="font-bold text-brand-blue">+ Outro / Personalizado</option>
                  </select>
                  {!isReadOnly && <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none text-gray-500"><svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" /></svg></div>}
                </div>
                {cultSelectValue === 'custom' && !isReadOnly && (<input className="w-full border p-2 rounded mt-2 text-sm animate-fade-in" placeholder="Digite o nome do evento..." value={title} onChange={e => setTitle(e.target.value)} autoFocus />)}
              </div>
              <div><label className="block text-sm font-bold text-gray-600 mb-1">Data</label><input type="date" value={date} onChange={e => setDate(e.target.value)} disabled={isReadOnly} className={`w-full border p-2 rounded ${isReadOnly ? 'bg-gray-100 text-gray-500' : ''}`} required /></div>
              <div><label className="block text-sm font-bold text-gray-600 mb-1">Horário</label><input type="time" value={time} onChange={e => setTime(e.target.value)} disabled={isReadOnly} className={`w-full border p-2 rounded ${isReadOnly ? 'bg-gray-100 text-gray-500' : ''}`} required /></div>
            </div>
            <hr />
            <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 min-w-0 ${isReadOnly ? 'opacity-50 pointer-events-none grayscale' : ''}`}>
              <div className="min-w-0"><h4 className="font-bold text-gray-700 mb-2">1. Adicionar Integrante:</h4><div className="max-h-60 overflow-y-auto border p-2 rounded bg-gray-50 grid grid-cols-2 gap-2">{musicians.map(m => {
                const isSelected = selectedTeam.some(item => item.id === m.id);
                const isBusy = busyMusicians.includes(m.id);

                // NOVO: Lógica visual de bloqueio considerando Domingo
                let isUnavailable = false;
                const unavailRecord = unavailableMusicians.find(u => u.id === m.id);
                if (unavailRecord) {
                  isUnavailable = true;
                  if (date && time) {
                    const [y, mo, d] = date.split('-');
                    const dateObj = new Date(y, mo - 1, d);
                    if (dateObj.getDay() === 0) {
                      const isMorningService = time < "14:00";
                      const isEveningService = time >= "14:00";
                      if (unavailRecord.servicePeriod === 'Manhã' && isEveningService) isUnavailable = false;
                      if (unavailRecord.servicePeriod === 'Noite' && isMorningService) isUnavailable = false;
                    }
                  }
                }

                return (<div key={m.id} onClick={() => !isSelected && !isReadOnly && addMusicianToTeam(m)} className={`p-2 rounded text-xs border cursor-pointer transition relative ${isSelected ? 'bg-gray-200 text-gray-400 cursor-default' : isBusy ? 'bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed' : isUnavailable ? 'bg-red-50 border-red-200 hover:bg-red-100' : 'bg-white hover:border-brand-blue'}`} title={isBusy ? "Já escalado neste horário" : ""}><span className="font-bold block truncate">{m.name}</span>{isSelected && <span className="text-[10px] font-bold text-green-600 flex items-center gap-1"><FaCheck /> Adicionado</span>}{isUnavailable && !isSelected && <span className="text-[10px] font-bold text-red-500 block">⚠️ Indisponível</span>}{isBusy && !isSelected && <span className="text-[10px] font-bold text-gray-500 flex items-center gap-1"><FaUserClock /> Ocupado</span>}</div>);
              })}</div></div>
              <div className="bg-blue-50 p-3 sm:p-4 rounded border border-blue-100 min-w-0"><h4 className="font-bold text-brand-blue mb-2">2. Equipe ({selectedTeam.length}):</h4>{selectedTeam.length === 0 ? <p className="text-sm text-gray-400 italic">Ninguém selecionado.</p> : (<div className="space-y-2 max-h-60 overflow-y-auto pr-2">{selectedTeam.map((member) => (<div key={member.id} className="flex items-center justify-between bg-white p-2 rounded shadow-sm gap-2"><div className="flex-1 min-w-0"><span className="block font-bold text-sm text-gray-800 truncate">{member.name}</span></div><div className="flex-shrink-0"><select disabled={isReadOnly} value={member.role} onChange={(e) => changeMusicianRole(member.id, e.target.value)} className="text-xs border p-1 rounded bg-gray-50 focus:border-brand-blue outline-none">{member.availableRoles && Array.isArray(member.availableRoles) ? (member.availableRoles.map((role, idx) => <option key={idx} value={role}>{role}</option>)) : (<option value={member.role}>{member.role}</option>)}</select></div>{!isReadOnly && <button onClick={() => removeMusicianFromTeam(member.id)} className="text-red-400 hover:text-red-600 p-1 flex-shrink-0"><FaTimes /></button>}</div>))}</div>)}</div>
            </div>
            <hr />
            <div className="min-w-0">
              <h4 className="font-bold text-brand-blue mb-3">Repertório:</h4>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
                <div className="relative w-full sm:max-w-xs">
                  <FaSearch className="absolute left-3 top-3 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Digite 3 letras..."
                    className="w-full pl-10 p-2 border rounded focus:border-brand-blue outline-none text-sm"
                    value={songSearchTerm}
                    onChange={(e) => setSongSearchTerm(e.target.value)}
                  />
                </div>
                <div className="text-xs text-gray-500 font-medium">{selectedSongs.length} selecionadas</div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 min-w-0">
                <div className="min-w-0">
                  <p className="text-xs font-bold text-gray-500 uppercase mb-2">Resultados:</p>
                  <div className="border p-2 rounded bg-gray-50 min-h-[100px] max-h-60 overflow-y-auto">
                    {songSearchTerm.length < 3 ? (
                      <p className="text-sm text-gray-400 text-center mt-4">Digite para buscar...</p>
                    ) : filteredSongsList.length === 0 ? (
                      <p className="text-sm text-gray-400 text-center mt-4">Nenhuma encontrada.</p>
                    ) : (
                      <div className="grid gap-2">
                        {filteredSongsList.map(s => {
                          const isSelected = selectedSongs.some(sel => sel.id === s.id);
                          return (
                            <div
                              key={s.id}
                              onClick={() => toggleSong(s)}
                              className={`cursor-pointer p-2 rounded text-sm border flex justify-between items-center gap-2 transition ${isSelected ? 'bg-blue-100 border-blue-300' : 'bg-white hover:border-brand-gold'}`}
                            >
                              <div className="min-w-0 flex-1">
                                <span className="font-bold block text-gray-800 truncate">{s.title}</span>
                                {s.artist && (
                                  <span className="block text-xs text-gray-400 truncate">
                                    {s.artist}
                                  </span>
                                )}
                              </div>
                              {isSelected ? (
                                <FaCheck className="text-brand-blue flex-shrink-0" />
                              ) : (
                                <span className="text-xs bg-gray-200 px-1.5 py-0.5 rounded font-bold flex-shrink-0">{s.key}</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
                <div className="bg-yellow-50 p-3 sm:p-4 rounded border border-yellow-100 min-w-0">
                  <p className="text-xs font-bold text-yellow-800 uppercase mb-2">Músicas na Escala:</p>
                  {selectedSongs.length === 0 ? (
                    <p className="text-sm text-gray-400 italic">Nenhuma selecionada.</p>
                  ) : (
                    <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                      {selectedSongs.map((s, idx) => (
                        <div key={s.id} className="flex items-center justify-between bg-white p-2 rounded shadow-sm border border-yellow-200 gap-2 min-w-0">
                          <div className="flex items-start gap-2 min-w-0 flex-1">
                            <span className="text-xs font-bold text-gray-400 mt-0.5 flex-shrink-0">{idx + 1}.</span>
                            <div className="min-w-0 flex-1">
                              <span className="text-sm font-bold text-gray-700 block truncate">{s.title}</span>
                              {s.artist && (
                                <span className="block text-xs text-gray-400 truncate">
                                  {s.artist}
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                            {/* Select de Tom (Key) */}
                            <select
                              value={s.customKey || s.key || ''}
                              onChange={(e) => updateSongKey(s.id, e.target.value)}
                              className="w-14 sm:w-16 text-center text-xs font-bold p-1 border rounded bg-gray-50 focus:border-brand-blue outline-none cursor-pointer"
                            >
                              <option value="?">?</option>
                              <optgroup label="Maiores">
                                {['C', 'C#', 'Db', 'D', 'Eb', 'E', 'F', 'F#', 'Gb', 'G', 'Ab', 'A', 'Bb', 'B'].map(k => <option key={k} value={k}>{k}</option>)}
                              </optgroup>
                              <optgroup label="Menores">
                                {['Cm', 'C#m', 'Dbm', 'Dm', 'Ebm', 'Em', 'Fm', 'F#m', 'Gbm', 'Gm', 'Abm', 'Am', 'Bbm', 'Bm'].map(k => <option key={k} value={k}>{k}</option>)}
                              </optgroup>
                            </select>

                            <button
                              type="button"
                              onClick={() => toggleSong(s)}
                              className="text-red-400 hover:text-red-600 p-1 flex-shrink-0"
                              title="Remover"
                            >
                              <FaTimes />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4 border-t">
              {editId && (
                <button type="button" onClick={handleCancel} className="w-full sm:w-auto bg-gray-200 px-6 py-3 rounded font-bold">
                  Cancelar
                </button>
              )}
              <button type="submit" className="w-full sm:w-auto bg-brand-gold text-brand-blue font-bold px-8 py-3 rounded shadow">
                {editId ? 'Atualizar' : 'Salvar'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* LISTAGEM */}
      {loading ? <div className="text-center p-10">Carregando agenda...</div> : (
        <div className="space-y-6 pb-20">
          {displaySchedules.length === 0 ? <div className="text-center py-12 text-gray-400">Nenhuma escala encontrada para este mês.</div> : displaySchedules.map(sched => {
            const isPast = isPastDate(sched.date);
            const canEdit = canEditSchedule(sched);
            const hasMinister = sched.team && sched.team.some(m => m.role === 'Ministro de Música');
            const isMissingSomething = (!sched.repertoire || sched.repertoire.length === 0) ||
              (!sched.team || sched.team.length === 0) ||
              !hasMinister;

            return (
              <div key={sched.id} className={`rounded-xl shadow-md overflow-hidden border-l-8 transition-all ${isPast ? 'bg-gray-100 border-gray-400 opacity-80' : 'bg-white border-brand-gold'}`}>
                <div className={`p-4 border-b flex justify-between items-start ${isPast ? 'bg-gray-200' : 'bg-gray-50'}`}>
                  <div><div className="flex items-center gap-2"><h3 className={`text-xl font-bold ${isPast ? 'text-gray-600' : 'text-brand-blue'}`}>{sched.title}</h3>{isMissingSomething && !isPast && <span className="text-orange-500 text-xl animate-pulse" title="Atenção: Incompleto"><FaExclamationTriangle /></span>}{isPast && <span className="bg-gray-500 text-white text-xs px-2 py-1 rounded flex items-center gap-1"><FaHistory /> Realizado</span>}</div><p className="text-gray-600 font-medium flex items-center gap-2">{formatDate(sched.date)} • {sched.time}h</p></div>
                  <div className="flex gap-2">{!isPast && <a href={generateWhatsappLink(sched)} target="_blank" rel="noreferrer" className="bg-green-500 hover:bg-green-600 text-white p-2 rounded-full transition"><FaWhatsapp size={20} /></a>}{canEdit && !isPast && <button onClick={() => handleEdit(sched)} className="bg-blue-100 text-blue-600 hover:bg-blue-200 p-2 rounded-full transition"><FaEdit /></button>}{userDetails?.isAdmin && <button onClick={() => handleDelete(sched.id)} className="bg-red-100 text-red-400 hover:bg-red-200 p-2 rounded-full transition"><FaTrash /></button>}</div>
                </div>
                <div className="p-4 grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-2">Repertório</h4>
                    <ul className="space-y-2">
                       {sched.repertoire && sched.repertoire.length > 0 ? (
                        sched.repertoire.map((song, idx) => {
                          const liveSong = songs.find(s => s.id === song.id) || {};
                          const youtubeLink = liveSong.link || song.link || '';
                          const cifraLink = liveSong.linkCifra || song.linkCifra || '';
                          const driveLink = liveSong.linkDrive || song.linkDrive || '';
                          const versaoLink = liveSong.linkVersao || song.linkVersao || '';
                          const artist = liveSong.artist || song.artist || '';
                          return (
                            <li key={idx} className="flex flex-col bg-white/50 border border-gray-100 p-3 rounded hover:bg-gray-50 transition-colors">
                              <div className="flex items-start justify-between w-full gap-2">
                                <div className="min-w-0 flex-1">
                                  <span className="font-bold text-gray-700 text-sm">{idx + 1}. {song.title}</span>
                                  {artist && (
                                    <span className="block sm:inline sm:ml-1 text-xs sm:text-sm text-gray-400">
                                      <span className="hidden sm:inline">— </span>{artist}
                                    </span>
                                  )}
                                </div>
                                <span className={`text-xs font-bold px-2 py-0.5 rounded flex-shrink-0 mt-0.5 ${isPast ? 'bg-gray-400 text-white' : 'bg-brand-blue text-white'}`}>
                                  {song.customKey || song.key || '?'}
                                </span>
                              </div>
                              {/* Linha de Links abaixo da música */}
                              {(youtubeLink || cifraLink || driveLink || versaoLink) && (
                                <div className="flex items-center gap-4 mt-2">
                                  {youtubeLink && <a href={youtubeLink} target="_blank" rel="noreferrer" className="text-red-500 hover:opacity-80 transition" title="YouTube"><FaYoutube size={16} /></a>}
                                  {cifraLink && <a href={cifraLink} target="_blank" rel="noreferrer" className="text-orange-500 hover:opacity-80 transition" title="Cifra"><FaGuitar size={16} /></a>}
                                  {driveLink && <a href={driveLink} target="_blank" rel="noreferrer" className="text-blue-500 hover:opacity-80 transition" title="Google Drive"><FaCloud size={16} /></a>}
                                  {versaoLink && <a href={versaoLink} target="_blank" rel="noreferrer" className="text-purple-500 hover:opacity-80 transition" title="Nossa Versão"><FaHeadphones size={16} /></a>}
                                </div>
                              )}
                            </li>
                          );
                        })
                      ) : (
                        <p className="text-sm text-gray-400 italic">Músicas ainda não definidas.</p>
                      )}
                    </ul>
                  </div>
                  <div><h4 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-2">Equipe</h4><div className="flex flex-wrap gap-2">{sched.team && sortTeam(sched.team).map((member, idx) => (<span key={idx} className={`text-sm px-3 py-1 rounded-full border flex items-center gap-1 ${getMemberStyle(member)}`}><strong>{member.name}</strong> <span className="text-xs opacity-70">({member.role})</span></span>))}</div></div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}